import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import ReactPlayer from 'react-player';

import useScrollPosition from '../hooks/useScrollPosition';
import getDynamicVideoPlayerControls from '../helpers/getDynamicVideoPlayerControls';

import 'react-video-seek-slider/styles.css';

import styles from '../styles/video.module.scss';

const Video = ({
  canPlayPip = true,
  captionsMode = 'showing',
  config = {},
  duration = 0,
  fullscreen = false,
  handleBookmark = () => {},
  handleFullScreen = () => {},
  handleSeekChange = () => {},
  handleSeekBarChange = () => {},
  handleSetPlaybackRate = () => {},
  handleToggleCaptions = () => {},
  handleTogglePIP = () => {},
  handleTogglePlaying = () => {},
  handleVolumeChange = () => {},
  hasBookmark = true,
  hasCaptions = false,
  id = 'video-player',
  innerRef = null,
  onDuration = () => {},
  onEnded = () => {},
  onProgress = () => {},
  onReady = () => {},
  onStart = () => {},
  pip = false,
  played = 0,
  playing = false,
  playbackRate = 1,
  screenSize = 'desktop',
  shouldHideCustomControls = false,
  shouldNotFloat = false,
  url = '',
  volume = 0.8,
}) => {
  const [videoPlayerHeight, setVideoPlayerHeight] = useState(0);
  const [videoPlayerTop, setVideoPlayerTop] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const ref = useRef(null);
  const resizeObserver = useRef(null);

  useEffect(() => {
    const element = ref.current;

    resizeObserver.current = new ResizeObserver((entries) => {
      setVideoPlayerHeight(entries[0].contentBoxSize[0].blockSize);
    });

    setVideoPlayerTop(element.getBoundingClientRect().top);
    resizeObserver.current.observe(element);

    return () => resizeObserver.current?.disconnect();
  }, []);

  const scrollPosition = useScrollPosition();

  const isFloating = !shouldNotFloat && scrollPosition > videoPlayerTop + videoPlayerHeight;

  const ControlsElement = getDynamicVideoPlayerControls({
    screenSize,
    isFloating,
  });

  const hoverTimeoutId = useRef(null);

  const handleMouseEvents = (type) => {
    const isMouseMove = type === 'move';
    
    clearTimeout(hoverTimeoutId.current);
    setIsHovering(isMouseMove);

    if (isMouseMove) {
      hoverTimeoutId.current = setTimeout(() => setIsHovering(false), 3000);
    }
  };

  return (
    <div
      className={classnames(styles.videoContainer, {
        [styles.isHovering]: isHovering,
        [styles.isFloating]: isFloating,
        [styles.isFullScreen]: fullscreen,
      })}
      id={id}
      ref={ref}
      onFocus={() => handleMouseEvents('move')}
      onMouseMove={() => handleMouseEvents('move')}
      onMouseLeave={() => handleMouseEvents('leave')}
    >
      <ReactPlayer
        className={styles.container}
        config={config}
        onReady={onReady}
        onStart={onStart}
        ref={innerRef}
        width="100%"
        height="100%"
        url={url}
        pip={pip}
        playing={playing}
        controls={shouldHideCustomControls}
        loop={false}
        playbackRate={playbackRate}
        progressInterval={100}
        volume={volume}
        onPlay={() => handleTogglePlaying({ shouldPlay: true })}
        onPause={() => handleTogglePlaying({ shouldPlay: false })}
        onProgress={onProgress}
        onDuration={onDuration}
        onEnded={onEnded}
        onContextMenu={(event) => {
          event.preventDefault();
        }}
      />
      {!shouldHideCustomControls && (
        <div className={styles.controlsContainer}>
          <ControlsElement
            canPlayPip={canPlayPip}
            handleTogglePlaying={handleTogglePlaying}
            handleSeekChange={handleSeekChange}
            handleSetPlaybackRate={handleSetPlaybackRate}
            handleFullScreen={handleFullScreen}
            handleTogglePIP={handleTogglePIP}
            handleSeekBarChange={handleSeekBarChange}
            handleVolumeChange={handleVolumeChange}
            handleToggleCaptions={handleToggleCaptions}
            handleBookmark={handleBookmark}
            playing={playing}
            playbackRate={playbackRate}
            played={played}
            duration={duration}
            fullscreen={fullscreen}
            hasBookmark={hasBookmark}
            hasCaptions={hasCaptions}
            captionsMode={captionsMode}
            volume={volume}
          />
        </div>
      )}
    </div>
  );
};

Video.propTypes = {
  canPlayPip: PropTypes.bool,
  config: PropTypes.shape({}),
  duration: PropTypes.number,
  fullscreen: PropTypes.bool,
  handleBookmark: PropTypes.func,
  handleFullScreen: PropTypes.func,
  handleSeekChange: PropTypes.func,
  handleSeekBarChange: PropTypes.func,
  handleSetPlaybackRate: PropTypes.func,
  handleToggleCaptions: PropTypes.func,
  handleTogglePIP: PropTypes.func,
  handleTogglePlaying: PropTypes.func,
  handleVolumeChange: PropTypes.func,
  hasBookmark: PropTypes.bool,
  hasCaptions: PropTypes.bool,
  innerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
    PropTypes.object,
  ]),
  onDuration: PropTypes.func,
  onEnded: PropTypes.func,
  onProgress: PropTypes.func,
  onReady: PropTypes.func,
  onStart: PropTypes.func,
  pip: PropTypes.bool,
  played: PropTypes.number,
  playing: PropTypes.bool,
  playbackRate: PropTypes.number,
  screenSize: PropTypes.string,
  shouldHideCustomControls: PropTypes.bool,
  shouldNotFloat: PropTypes.bool,
  url: PropTypes.string,
  volume: PropTypes.number,
};

export default React.memo(Video);
